import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Maximize2 } from "lucide-react";
import SectionFadeIn from "@/components/SectionFadeIn";
import { buildMuralQuoteUrl } from "@/lib/quotePrefill";
import { getRouteState } from "@/lib/navigation";

const MATERIALS = [
  { id: "standard", label: "Standard Paste-the-Wall", rate: 14, desc: "Classic wallpaper-style finish, best value" },
  { id: "textured", label: "Premium Textured", rate: 18, desc: "Richer surface feel for statement walls" },
  { id: "peel", label: "Peel & Stick", rate: 22, desc: "Self-adhesive, simpler install" },
];

const UNITS = [
  { id: "cm", label: "cm", factor: 0.01 },
  { id: "m", label: "m", factor: 1 },
  { id: "mm", label: "mm", factor: 0.001 },
];

export function MuralLandingConfigurator() {
  const location = useLocation();
  const [wallWidth, setWallWidth] = useState("");
  const [wallHeight, setWallHeight] = useState("");
  const [unit, setUnit] = useState("cm");
  const [material, setMaterial] = useState("standard");

  const unitFactor = UNITS.find((u) => u.id === unit)?.factor || 0.01;
  const w = parseFloat(wallWidth) || 0;
  const h = parseFloat(wallHeight) || 0;
  const areaSqm = w * unitFactor * (h * unitFactor);
  const selectedMat = MATERIALS.find((m) => m.id === material) || MATERIALS[0];
  const estimatedPrice = areaSqm > 0 ? areaSqm * selectedMat.rate : 0;
  const dimensionsText =
    areaSqm > 0
      ? `${(w * unitFactor).toFixed(1)}m \u00d7 ${(h * unitFactor).toFixed(1)}m`
      : "";
  const configQuoteHref = buildMuralQuoteUrl({
    dimensionsText,
    materialLabel: selectedMat.label,
    estimatedPrice,
  });

  return (
    <section
      id="mural-configurator"
      className="py-20 md:py-24 bg-brand-surface/30"
      data-testid="mural-configurator"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionFadeIn>
          <div className="max-w-lg">
            <p className="text-[11px] tracking-[0.25em] uppercase font-semibold text-brand-gold mb-3">
              Price Your Mural
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-brand-text leading-tight">
              Made to Measure
            </h2>
            <p className="mt-2 text-[14px] text-brand-text-secondary leading-relaxed">
              Enter your wall dimensions and pick a material to see your estimated price instantly.
            </p>
          </div>
        </SectionFadeIn>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14">
          {/* LEFT: inputs */}
          <div className="space-y-7">
            {/* dimensions row */}
            <div>
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-brand-text-secondary/70 mb-3">
                Wall Dimensions
              </p>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-[11px] text-brand-text-secondary/50 mb-1">Width</label>
                  <input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    value={wallWidth}
                    onChange={(e) => setWallWidth(e.target.value)}
                    placeholder={unit === "cm" ? "e.g. 320" : unit === "m" ? "e.g. 3.2" : "e.g. 3200"}
                    className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-2.5 outline-none transition-colors duration-300 text-[15px] placeholder:text-brand-text-secondary/25"
                    data-testid="config-width"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-[11px] text-brand-text-secondary/50 mb-1">Height</label>
                  <input
                    type="number"
                    min="0"
                    inputMode="decimal"
                    value={wallHeight}
                    onChange={(e) => setWallHeight(e.target.value)}
                    placeholder={unit === "cm" ? "e.g. 240" : unit === "m" ? "e.g. 2.4" : "e.g. 2400"}
                    className="w-full bg-transparent border-b border-brand-border focus:border-brand-gold text-brand-text py-2.5 outline-none transition-colors duration-300 text-[15px] placeholder:text-brand-text-secondary/25"
                    data-testid="config-height"
                  />
                </div>
                <div className="w-20 shrink-0">
                  <label className="block text-[11px] text-brand-text-secondary/50 mb-1">Unit</label>
                  <div className="flex border-b border-brand-border">
                    {UNITS.map((u) => (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setUnit(u.id)}
                        className={`flex-1 py-2.5 text-[13px] font-medium transition-colors ${
                          unit === u.id
                            ? "text-brand-gold"
                            : "text-brand-text-secondary/40 hover:text-brand-text-secondary"
                        }`}
                        data-testid={`config-unit-${u.id}`}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* material selector */}
            <div>
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-brand-text-secondary/70 mb-3">
                Material
              </p>
              <div className="space-y-2">
                {MATERIALS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setMaterial(m.id)}
                    className={`w-full flex items-center justify-between px-5 py-4 rounded-sm border transition-[border-color,color,background-color] duration-200 text-left ${
                      material === m.id
                        ? "border-brand-gold bg-brand-gold/5"
                        : "border-brand-border hover:border-brand-text-secondary/30"
                    }`}
                    data-testid={`config-material-${m.id}`}
                  >
                    <div>
                      <span className={`block text-[14px] font-medium ${
                        material === m.id ? "text-brand-gold" : "text-brand-text"
                      }`}>
                        {m.label}
                      </span>
                      <span className="block text-[11px] text-brand-text-secondary/50 mt-0.5">
                        {m.desc}
                      </span>
                    </div>
                    <span className={`text-[13px] font-medium tabular-nums shrink-0 ml-4 ${
                      material === m.id ? "text-brand-gold" : "text-brand-text-secondary/60"
                    }`}>
                      &pound;{m.rate}/m&sup2;
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT: price summary */}
          <div className="flex flex-col justify-center">
            <div className="bg-brand-bg border border-brand-border rounded-sm px-8 py-8" data-testid="config-summary">
              {areaSqm > 0 ? (
                <>
                  <div className="flex items-center gap-2 text-brand-text-secondary/60 text-[13px] mb-1">
                    <Maximize2 size={14} className="text-brand-gold" />
                    <span>
                      {(w * unitFactor).toFixed(1)}m &times; {(h * unitFactor).toFixed(1)}m = {areaSqm.toFixed(2)} m&sup2;
                    </span>
                  </div>
                  <div className="text-[13px] text-brand-text-secondary/60 mb-5">
                    {selectedMat.label} at &pound;{selectedMat.rate}/m&sup2;
                  </div>
                  <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-brand-gold mb-1">
                    Estimated Price
                  </p>
                  <p className="font-heading text-4xl sm:text-5xl text-brand-text tracking-tight" data-testid="config-price">
                    &pound;{estimatedPrice.toFixed(2)}
                  </p>
                  <p className="mt-3 text-[11px] text-brand-text-secondary/40 leading-snug">
                    Final price confirmed after design review. Includes print and material &mdash; installation quoted separately if needed.
                  </p>
                  <Link
                    to={configQuoteHref}
                    state={getRouteState(location)}
                    className="inline-flex items-center gap-2 mt-6 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide"
                    data-testid="config-quote-cta"
                  >
                    Request Quote With These Measurements <ArrowRight size={15} />
                  </Link>
                </>
              ) : (
                <div className="text-center py-6">
                  <Maximize2 size={28} strokeWidth={1} className="mx-auto text-brand-border/40 mb-3" />
                  <p className="text-[14px] text-brand-text-secondary/40">
                    Enter your wall width and height to see your estimated mural price.
                  </p>
                  <p className="mt-2 text-[12px] text-brand-text-secondary/30">
                    From &pound;14/m&sup2; depending on material
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
